import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import MovieCard from "@/components/base/MovieCard";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useFavorites } from "@/hooks/useFavorites";
import { getMovieById, getRelatedMovies } from "@/utils/movie";

export default function DetailPage() {
  const { id } = useParams();
  const movie = getMovieById(id);
  const { isFavorite, toggleFavorite } = useFavorites();

  usePageMeta({
    title: movie ? `${movie.title} - 大师兄影视` : "影视详情 - 大师兄影视",
    description: movie
      ? `${movie.title}（${movie.year}·${movie.region}）：${movie.description.slice(0, 60)}… 大师兄影视提供影片资料与观影提示。`
      : "大师兄影视影视详情页",
    keywords: movie ? `${movie.title},${movie.genres.join(",")},大师兄影视,影视详情` : "大师兄影视",
  });

  if (!movie) {
    return (
      <SiteLayout>
        <div className="w-full px-4 md:px-8 py-24 flex flex-col items-center justify-center text-center">
          <span className="w-16 h-16 flex items-center justify-center rounded-full bg-secondary-100 mb-4">
            <i className="ri-emotion-sad-line text-3xl text-secondary-600" />
          </span>
          <h1 className="font-heading text-xl font-bold text-foreground-950">未找到该影视作品</h1>
          <p className="mt-2 text-sm text-foreground-600">该作品可能已下架或链接有误</p>
          <Link
            to="/"
            className="mt-5 inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-home-4-line" />
            返回首页
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const related = getRelatedMovies(movie, 6);
  const favorite = isFavorite(movie.id);

  const infoRows = [
    { label: "导演", value: movie.director, icon: "ri-clapperboard-line" },
    { label: "类型", value: movie.type, icon: "ri-movie-2-line" },
    { label: "地区", value: movie.region, icon: "ri-map-pin-line" },
    { label: "年份", value: String(movie.year), icon: "ri-calendar-line" },
    { label: "时长", value: movie.duration, icon: "ri-time-line" },
    { label: "状态", value: movie.status, icon: "ri-live-line" },
  ];

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-8 pt-6 md:pt-8">
        <nav className="flex items-center gap-1.5 text-xs md:text-sm text-foreground-600 mb-4 flex-wrap" aria-label="面包屑导航">
          <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
          <i className="ri-arrow-right-s-line" />
          <Link to={`/category/${encodeURIComponent(movie.type)}`} className="hover:text-primary-600 transition-colors cursor-pointer">
            {movie.type}
          </Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-950 font-medium truncate max-w-[10rem]">{movie.title}</span>
        </nav>
      </div>

      <section className="relative w-full bg-gradient-to-r from-primary-600 to-primary-800">
        <div className="relative w-full px-4 md:px-8 py-7 md:py-10">
          <div className="flex flex-col md:flex-row gap-5 md:gap-8">
            <div className="w-40 md:w-56 shrink-0 mx-auto md:mx-0">
              <div className="w-full aspect-[2/3] rounded-lg overflow-hidden bg-background-200 border-4 border-background-50">
                <img
                  src={movie.cover}
                  alt={`${movie.title} 海报`}
                  title={`${movie.title} - 大师兄影视`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0 md:pt-2">
              <h1 className="font-heading text-2xl md:text-4xl font-extrabold text-background-50">{movie.title}</h1>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className="flex items-center gap-1 px-2 py-1 rounded-md bg-accent-500 text-background-50 text-xs font-semibold">
                  <i className="ri-star-fill" />
                  {movie.rating.toFixed(1)}
                </span>
                {movie.genres.map((g) => (
                  <Link
                    key={g}
                    to={`/search?q=${encodeURIComponent(g)}`}
                    className="px-2 py-1 rounded-md bg-background-50/15 text-background-50 text-xs hover:bg-background-50/25 transition-colors cursor-pointer"
                  >
                    {g}
                  </Link>
                ))}
              </div>

              <p className="mt-4 text-sm md:text-[15px] text-background-100/90 leading-6 line-clamp-3">{movie.description}</p>

              <div className="mt-5 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  disabled
                  className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-background-50 text-primary-700 text-sm font-semibold whitespace-nowrap cursor-not-allowed opacity-90"
                >
                  <i className="ri-play-circle-line text-lg" />
                  请前往正规平台观看
                </button>
                <button
                  type="button"
                  onClick={() => toggleFavorite(movie.id)}
                  className={`inline-flex items-center gap-2 h-11 px-6 rounded-md text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                    favorite
                      ? "bg-accent-500 text-background-50 hover:bg-accent-600"
                      : "bg-background-50/10 border border-background-50/40 text-background-50 hover:bg-background-50/20"
                  }`}
                >
                  <i className={favorite ? "ri-heart-3-fill" : "ri-heart-3-line"} />
                  {favorite ? "已收藏" : "收藏"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="w-full px-4 md:px-8 py-6 md:py-8 flex flex-col lg:flex-row gap-6">
        <div className="flex-1 min-w-0 space-y-6">
          <section className="rounded-lg bg-background-50 border border-background-200 p-5 md:p-6">
            <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-3 flex items-center gap-2">
              <span className="w-1 h-5 rounded-full bg-accent-500" />
              剧情简介
            </h2>
            <p className="text-sm md:text-[15px] text-foreground-700 leading-7">{movie.description}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {movie.tags.map((t) => (
                <span key={t} className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-900 text-xs">
                  #{t}
                </span>
              ))}
            </div>
          </section>

          <section className="rounded-lg bg-background-50 border border-background-200 p-5 md:p-6">
            <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-4 flex items-center gap-2">
              <span className="w-1 h-5 rounded-full bg-accent-500" />
              影片信息
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">
              {infoRows.map((row) => (
                <div key={row.label} className="flex items-center gap-3">
                  <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 shrink-0">
                    <i className={`${row.icon} text-base`} />
                  </span>
                  <div className="min-w-0">
                    <p className="text-xs text-foreground-500">{row.label}</p>
                    <p className="text-sm font-medium text-foreground-950 truncate">{row.value}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-5 pt-5 border-t border-background-200">
              <p className="text-xs text-foreground-500 mb-2">主演</p>
              <div className="flex flex-wrap gap-2">
                {movie.cast.map((c) => (
                  <span key={c} className="px-3 py-1.5 rounded-full bg-background-100 text-foreground-800 text-sm">
                    <i className="ri-user-star-line mr-1 text-primary-500" />
                    {c}
                  </span>
                ))}
              </div>
            </div>
          </section>

          <section className="rounded-lg bg-accent-100/70 border border-accent-200 p-5 md:p-6 flex items-start gap-4">
            <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-accent-500 text-background-50 shrink-0">
              <i className="ri-information-line text-xl" />
            </span>
            <div>
              <h2 className="font-heading text-base md:text-lg font-bold text-accent-900">观影提示</h2>
              <p className="mt-1.5 text-sm text-accent-900/80 leading-6">
                大师兄影视仅提供影视作品的信息展示与资料查询，不提供任何在线播放或下载功能。
                如需观看《{movie.title}》，请前往正规影视平台搜索观看，支持正版内容。
              </p>
            </div>
          </section>
        </div>

        <aside className="w-full lg:w-72 lg:shrink-0">
          <div className="lg:sticky lg:top-24 rounded-lg bg-background-50 border border-background-200 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950 mb-4 flex items-center gap-2">
              <i className="ri-bookmark-3-line text-primary-600" />
              内容速览
            </h2>
            <ul className="space-y-3 text-sm">
              <li className="flex items-center justify-between">
                <span className="text-foreground-600">综合评分</span>
                <span className="font-semibold text-accent-600">{movie.rating.toFixed(1)} 分</span>
              </li>
              <li className="flex items-center justify-between">
                <span className="text-foreground-600">热度</span>
                <span className="font-semibold text-foreground-950">{Math.round(movie.views / 10000)} 万</span>
              </li>
              <li className="flex items-center justify-between">
                <span className="text-foreground-600">更新于</span>
                <span className="font-semibold text-foreground-950">{movie.updatedAt}</span>
              </li>
              <li className="flex items-center justify-between">
                <span className="text-foreground-600">收藏状态</span>
                <span className={`font-semibold ${favorite ? "text-accent-600" : "text-foreground-600"}`}>
                  {favorite ? "已收藏" : "未收藏"}
                </span>
              </li>
            </ul>
            <button
              type="button"
              disabled
              className="mt-5 w-full h-10 rounded-md bg-secondary-100 text-secondary-700 text-sm font-medium flex items-center justify-center gap-2 cursor-not-allowed"
            >
              <i className="ri-download-2-line" />
              暂不提供下载
            </button>
          </div>
        </aside>
      </div>

      <div className="bg-background-100/60">
        <section className="w-full px-4 md:px-8 py-8 md:py-10">
          <div className="flex items-end justify-between gap-4 mb-5 md:mb-6">
            <div>
              <span className="inline-flex items-center gap-1.5 text-accent-600 text-xs font-semibold uppercase tracking-wider mb-1.5">
                <span className="w-4 h-0.5 bg-accent-500 rounded-full" />
                Related
              </span>
              <h2 className="font-heading text-xl md:text-2xl font-extrabold text-foreground-950">相关推荐</h2>
              <p className="mt-1 text-xs md:text-sm text-foreground-600">根据类型与题材为你匹配的同类作品</p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-5">
            {related.map((m) => (
              <MovieCard key={m.id} movie={m} />
            ))}
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}